import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CalendarClock, Send, Loader2, CheckCircle2, Settings2, Sparkles,
  Clock3, Mail as MailIcon, ChevronDown, ChevronUp
} from "lucide-react";
import { api } from "@/lib/auth";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const formatDKK = (n) => new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(n || 0);
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" });
};

export default function RemindersSection() {
  const [settings, setSettings] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [form, setForm] = useState({ reminder_subject: "", reminder_template: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, u, se] = await Promise.all([
        api.get("/admin/reminders/settings"),
        api.get("/admin/reminders/candidates"),
        api.get("/admin/reminders/upcoming"),
        api.get("/admin/reminders/sent"),
      ]);
      setSettings(s.data);
      setForm({ reminder_subject: s.data.reminder_subject, reminder_template: s.data.reminder_template });
      setCandidates(c.data);
      setUpcoming(u.data);
      setSent(se.data);
    } catch {
      toast.error("Kunne ikke hente reminder-data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleAuto = async (checked) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/reminders/settings", { auto_reminders_enabled: checked });
      setSettings(data);
      toast.success(checked ? "Automatisk afsendelse slået til" : "Automatisk afsendelse slået fra");
    } catch {
      toast.error("Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/reminders/settings", form);
      setSettings(data);
      setEditing(false);
      toast.success("Skabelon gemt");
    } catch {
      toast.error("Kunne ikke gemme");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async (quoteId, email) => {
    setSendingId(quoteId);
    try {
      await api.post(`/admin/reminders/send/${quoteId}`);
      toast.success(`Reminder sendt til ${email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally {
      setSendingId(null);
    }
  };

  if (loading || !settings) {
    return (
      <div className="rounded-3xl border border-brand-border bg-brand-surface p-10 grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <section data-testid="reminders-section" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-2 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Automation
          </div>
          <h2 className="font-display text-3xl tracking-tight text-brand-text">Sæson-reminders</h2>
          <p className="text-sm text-brand-muted mt-2 max-w-xl">
            Automatisk follow-up ca. ét år efter en udført klipning. Kunden får en personlig mail — du får bookingen tilbage uden at løfte en finger.
          </p>
        </div>
      </div>

      {/* Auto-toggle card */}
      <div className="rounded-3xl border border-brand-border bg-brand-surface p-6 lg:p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-brand-primary text-brand-bg grid place-items-center shrink-0">
              <CalendarClock className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display text-xl text-brand-text">Automatisk afsendelse</div>
              <p className="text-sm text-brand-muted mt-1 max-w-md">
                Når aktiv, kører et job hver dag kl. 08:00 (dansk tid) og sender reminders til kunder hvis sidste klipning var 11–13 måneder siden.
              </p>
              {settings.auto_reminders_enabled && (
                <div className="mt-3 inline-flex items-center gap-2 text-xs text-brand-primary bg-brand-primary/10 px-3 py-1.5 rounded-full">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Aktiv · næste kørsel i morgen kl. 08:00
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-brand-muted">{settings.auto_reminders_enabled ? "Til" : "Fra"}</span>
            <Switch
              data-testid="reminders-toggle"
              checked={settings.auto_reminders_enabled}
              onCheckedChange={toggleAuto}
              disabled={saving}
            />
          </div>
        </div>

        {/* Template editor */}
        <div className="mt-6 pt-6 border-t border-brand-border/60">
          <button
            type="button"
            onClick={() => setTemplateOpen(!templateOpen)}
            data-testid="reminders-template-toggle"
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-brand-text">
              <Settings2 className="h-4 w-4 text-brand-primary" />
              Rediger reminder-skabelon
            </div>
            {templateOpen ? <ChevronUp className="h-4 w-4 text-brand-muted" /> : <ChevronDown className="h-4 w-4 text-brand-muted" />}
          </button>

          {templateOpen && (
            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-xs">Emne</Label>
                <Input
                  data-testid="reminders-subject"
                  value={form.reminder_subject}
                  onChange={(e) => { setForm({ ...form, reminder_subject: e.target.value }); setEditing(true); }}
                  className="mt-1.5 bg-brand-bg"
                />
              </div>
              <div>
                <Label className="text-xs">Besked-tekst</Label>
                <Textarea
                  data-testid="reminders-template"
                  rows={6}
                  value={form.reminder_template}
                  onChange={(e) => { setForm({ ...form, reminder_template: e.target.value }); setEditing(true); }}
                  className="mt-1.5 bg-brand-bg"
                />
                <p className="text-xs text-brand-muted mt-2">
                  Kundens navn, tidligere pris og "Book en tid"-knap tilføjes automatisk til mailen.
                </p>
              </div>
              {editing && (
                <div className="flex gap-2">
                  <button
                    onClick={saveTemplate}
                    disabled={saving}
                    data-testid="reminders-save-template"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-5 py-2.5 text-sm font-medium hover:bg-brand-hover disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Gem skabelon
                  </button>
                  <button
                    onClick={() => { setForm({ reminder_subject: settings.reminder_subject, reminder_template: settings.reminder_template }); setEditing(false); }}
                    className="inline-flex items-center rounded-full border border-brand-border px-5 py-2.5 text-sm hover:bg-brand-soft"
                  >
                    Annullér
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Candidates ready */}
      <div className="rounded-3xl border border-brand-border bg-brand-surface overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between">
          <div>
            <div className="font-medium text-brand-text">Klar til afsendelse</div>
            <div className="text-xs text-brand-muted mt-0.5">
              {candidates.length} kunde{candidates.length !== 1 ? "r" : ""} har haft udført arbejde for 11–13 måneder siden.
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-medium">
            <Clock3 className="h-3 w-3" /> {candidates.length} i vindue
          </span>
        </div>

        {candidates.length === 0 ? (
          <div className="p-10 text-center text-sm text-brand-muted">
            Ingen kunder er klar lige nu. Kunder dukker op her, når de rammer 11 måneder efter udført klipning.
          </div>
        ) : (
          <div className="divide-y divide-brand-border/60">
            {candidates.map((c, i) => (
              <div
                key={c.quote_id}
                data-testid={`reminder-candidate-${i}`}
                className="px-6 py-4 flex items-center gap-4 flex-wrap"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-text">{c.name || c.email}</div>
                  <div className="text-xs text-brand-muted mt-0.5">
                    {c.email} · {c.length_meters} m · sidste besøg {formatDate(c.completed_at)} ({c.days_since} dage siden)
                  </div>
                </div>
                {c.estimated_price ? (
                  <div className="text-sm text-brand-primary font-medium">{formatDKK(c.estimated_price)}</div>
                ) : null}
                <button
                  onClick={() => sendNow(c.quote_id, c.email)}
                  disabled={sendingId === c.quote_id}
                  data-testid={`reminder-send-now-${i}`}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-4 py-2 text-sm font-medium hover:bg-brand-hover disabled:opacity-60"
                >
                  {sendingId === c.quote_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send nu
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming (getting close) */}
      {upcoming.length > 0 && (
        <div className="rounded-3xl border border-brand-border bg-brand-surface overflow-hidden">
          <div className="px-6 py-4 border-b border-brand-border">
            <div className="font-medium text-brand-text">Kommer snart</div>
            <div className="text-xs text-brand-muted mt-0.5">
              Kunder der nærmer sig et års-mærket (9–11 måneder siden udført klipning).
            </div>
          </div>
          <div className="divide-y divide-brand-border/60">
            {upcoming.filter((u) => !u.in_window).map((u, i) => (
              <div key={u.quote_id} data-testid={`reminder-upcoming-${i}`} className="px-6 py-3 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-text text-sm">{u.name || u.email}</div>
                  <div className="text-xs text-brand-muted mt-0.5">
                    {u.email} · sidste besøg {formatDate(u.completed_at)}
                  </div>
                </div>
                <div className="text-xs text-brand-muted">
                  om ca. {Math.max(0, 365 - u.days_since)} dage
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent history */}
      <div className="rounded-3xl border border-brand-border bg-brand-surface overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-border">
          <div className="font-medium text-brand-text">Sendte reminders</div>
          <div className="text-xs text-brand-muted mt-0.5">Historik over alle sæson-reminders — automatiske og manuelle.</div>
        </div>
        {sent.length === 0 ? (
          <div className="p-10 text-center text-sm text-brand-muted">
            Ingen sæson-reminders sendt endnu.
          </div>
        ) : (
          <div className="divide-y divide-brand-border/60">
            {sent.slice(0, 20).map((s, i) => (
              <div key={s.id} data-testid={`reminder-sent-${i}`} className="px-6 py-3 flex items-center gap-4 flex-wrap">
                <MailIcon className="h-4 w-4 text-brand-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-brand-text">{s.to_name || s.to_email}</div>
                  <div className="text-xs text-brand-muted">{s.to_email} · {s.subject}</div>
                </div>
                <div className="text-xs text-brand-muted">
                  {formatDate(s.sent_at)} · {s.triggered_by === "auto" ? "Automatisk" : "Manuelt"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
